import 'react-phone-input-2/lib/material.css';

import { useState } from 'react';
import { post } from '../../utils/rest';
import * as yup from 'yup';
import {
  Button,
  FormControl,
  FormHelperText,
  Grow,
  InputLabel,
  makeStyles,
  NativeSelect,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { useFormik } from 'formik';
import PhoneInput from 'react-phone-input-2';
import { Questionnaire } from '../../types/leads-types';
import { moneyFormat } from '../../components/Money';
import { VoidNoArgFunc } from '../../types/UtilTypes';

export interface QuestionnaireValues {
  orgId: string;
  name: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  referrerId: string;
  serviceId: string;
  clientScopeGoals: string;
  budgetOption: number;
}

const useStyles = makeStyles(() => ({
  fields: {
    maxWidth: 600,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingBottom: 16,
  },
  addressArea: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
  addressAreaField: {
    flex: 1,
  },
  select: {
    padding: 8,
    borderRadius: 4,
  },
}));

const schema = yup.object({
  name: yup.string().required('Please provide your name'),
  email: yup
    .string()
    .email('Email does not appear valid. Please check for typos.')
    .required('Please enter an email so we can get in touch'),
  phone: yup.string().required('Please enter a phone number'),
  address1: yup.string().required('Please enter an address'),
  city: yup.string().required('Please enter a city'),
  state: yup.string().required('Please enter a state'),
  zip: yup.string().required('Please enter a zip code'),
});

function submitQuestionnaire(values: QuestionnaireValues) {
  return post<QuestionnaireValues, any>('/guest/questionnaire/submit', values);
}

export interface FormLabelPack {
  name: string;
  email: string;
  service: string;
  clientScopeGoals: string;
  budget: string;
  referralChannel: string;
}

const DEFAULT_LABELS: FormLabelPack = {
  name: 'Your name',
  email: 'Your email',
  service: 'What service are you interested in?',
  clientScopeGoals: 'Tell us about your project scope and goals',
  budget: 'What is your overall budget?',
  referralChannel: 'How did you hear about us?',
};

interface SelectOption {
  label: string;
  value: any;
}

function RawSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  value: any;
  options: SelectOption[];
  onChange: (e: any) => void;
}) {
  const classes = useStyles();
  return (
    <FormControl fullWidth>
      <InputLabel variant='standard' htmlFor={label}>
        {label}
      </InputLabel>

      <NativeSelect
        value={value}
        variant='outlined'
        className={classes.select}
        onChange={onChange}
        inputProps={{
          id: label,
        }}
      >
        {options.map((option) => (
          <option value={option.value}>{option.label}</option>
        ))}
      </NativeSelect>
    </FormControl>
  );
}

export default function QuestionnaireComp({
  questionnaire,
  labelPack = DEFAULT_LABELS,
  submitter = submitQuestionnaire,
  onSubmitted = () => {},
}: {
  questionnaire: Questionnaire;
  labelPack: FormLabelPack;
  submitter: (values: QuestionnaireValues) => Promise<void>;
  onSubmitted?: VoidNoArgFunc;
}) {
  const classes = useStyles();
  const [submitted, setSubmitted] = useState(false);
  const formik = useFormik<QuestionnaireValues>({
    initialValues: {
      orgId: questionnaire.orgId,
      name: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      referrerId: questionnaire.referralChannels[0].id,
      serviceId: questionnaire.services[0].id,
      clientScopeGoals: '',
      budgetOption: 0,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      submitter(values).then(() => {
        setSubmitted(true);
        onSubmitted();
      });
    },
  });

  const { referralChannels, budgetRanges, services } = questionnaire;

  const {
    values,
    handleChange,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    isSubmitting,
  } = formik;

  if (submitted) {
    return (
      <Grow in={true}>
        <div
          style={{
            padding: 32,
            margin: '0 auto',
          }}
        >
          <h2>Thank you!</h2>
          <p>
            Thank you for submitting your inquiry. We will be in touch shortly.
          </p>
        </div>
      </Grow>
    );
  }

  return (
    <div className={classes.fields}>
      <TextField
        name='name'
        label={labelPack.name}
        variant='outlined'
        onChange={handleChange}
        value={values.name}
        error={touched.name && !!errors.name}
        helperText={touched.name && formik.errors.name}
      />

      <TextField
        label={labelPack.email}
        name='email'
        variant='outlined'
        value={values.email}
        error={touched.email && !!errors.email}
        helperText={touched.email && formik.errors.email}
        onChange={handleChange}
      />

      <div>
        <PhoneInput
          country={'us'}
          disableDropdown
          enableAreaCodes
          inputClass={touched.phone && !!errors.phone ? 'Mui-error' : ''}
          onChange={(phoneNum) => setFieldValue('phone', phoneNum)}
        />
        {touched.phone && !!errors.phone && (
          <FormHelperText error>{errors.phone}</FormHelperText>
        )}
      </div>

      <div>
        <Typography variant='subtitle1'>Project address</Typography>
        <Paper
          style={{
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
          variant='outlined'
        >
          <TextField
            label='Address'
            name='address1'
            placeholder='123 Easy St'
            fullWidth
            error={touched.address1 && !!errors.address1}
            onChange={handleChange}
          />
          <TextField
            label='Address (Line 2)'
            name='address2'
            placeholder='(Optional) Unit 3'
            fullWidth
            onChange={handleChange}
          />
          <div className={classes.addressArea}>
            <TextField
              label='City'
              name='city'
              placeholder='City'
              error={touched.city && !!errors.city}
              className={classes.addressAreaField}
              onChange={handleChange}
            />
            <TextField
              label='State'
              name='state'
              placeholder='State'
              error={touched.state && !!errors.state}
              className={classes.addressAreaField}
              onChange={handleChange}
            />
            <TextField
              label='ZIP'
              name='zip'
              placeholder='ZIP'
              error={touched.zip && !!errors.zip}
              className={classes.addressAreaField}
              onChange={handleChange}
            />
          </div>
        </Paper>
      </div>

      <TextField
        name='clientScopeGoals'
        label={labelPack.clientScopeGoals}
        multiline
        variant='outlined'
        onChange={handleChange}
        placeholder='Tell us about what you have in mind'
      />

      <RawSelect
        label={labelPack.service}
        value={values.serviceId}
        options={services.map((s) => {
          return {
            label: s.name,
            value: s.id,
          };
        })}
        onChange={(e: any) => setFieldValue('serviceId', e.target.value)}
      />

      {/* Checkbox group: what rooms are you looking to update?
    <Collapse in={remodel}>
      <FormGroup>
        {locations.map(loc => (
          <FormControlLabel control={<Checkbox />} label={loc} />
        ))}
      </FormGroup>
    </Collapse>
 */}
      <RawSelect
        label={labelPack.budget}
        value={values.budgetOption}
        options={budgetRanges.map((r, idx) => {
          if (r.unknown) {
            return {
              label: "Don't know yet",
              value: idx,
            };
          }
          if (r.high == null) {
            return {
              label: `Over ${moneyFormat(r.low)}`,
              value: idx,
            };
          }
          return {
            label: `${moneyFormat(r.low)} - ${moneyFormat(r.high)}`,
            value: idx,
          };
        })}
        onChange={(e: any) => {
          setFieldValue('budgetOption', e.target.value);
        }}
      />

      {/* What is your ideal start date?  */}
      {/* <DatePicker value={undefined} onChange={function (date: MaterialUiPickersDate): void {
      throw new Error('Function not implemented.')
    } } /> */}

      {/* photos of inpsiration
    any architectural drawings/plans?  */}
      <RawSelect
        label={labelPack.referralChannel}
        value={values.referrerId}
        options={referralChannels.map((c) => {
          return {
            label: c.name,
            value: c.id,
          };
        })}
        onChange={(e: any) => setFieldValue('referrerId', e.target.value)}
      />

      <Button
        variant='contained'
        size='large'
        onClick={() => handleSubmit()}
        disabled={isSubmitting}
      >
        {isSubmitting && <>Sending...</>}
        {!isSubmitting && <>Submit</>}
      </Button>
      {((touched.name && errors.name) ||
        (touched.email && errors.email) ||
        (touched.phone && errors.phone)) && (
        <span style={{ color: 'red' }}>
          There were errors, please see above
        </span>
      )}
    </div>
  );
}
